import React from "react";
import { Box, Button, Typography, SxProps, Theme } from "@mui/material";

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  sx?: SxProps<Theme>;
}

/**
 * EmptyState — shown when a list, table, or data section has no records.
 * Professional, non-childish, matches enterprise software aesthetics (Linear, Retool, Stripe).
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  secondaryAction,
  sx,
}) => {
  return (
    <Box
      role="region"
      aria-label={title}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 3,
        textAlign: "center",
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            bgcolor: "#F1F5F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            color: "text.disabled",
            "& .MuiSvgIcon-root": { fontSize: 24 },
          }}
        >
          {icon}
        </Box>
      )}

      <Typography
        fontSize="14px"
        fontWeight={600}
        color="text.primary"
        mb={0.5}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          fontSize="13px"
          color="text.secondary"
          mb={action ? 3 : 0}
          maxWidth={320}
        >
          {description}
        </Typography>
      )}

      {(action || secondaryAction) && (
        <Box display="flex" gap={1.5} mt={description ? 0 : 3}>
          {action && (
            <Button
              variant="contained"
              size="small"
              onClick={action.onClick}
              sx={{ borderRadius: "8px", fontWeight: 600, px: 2.5 }}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="outlined"
              size="small"
              onClick={secondaryAction.onClick}
              sx={{ borderRadius: "8px", fontWeight: 600, px: 2.5 }}
            >
              {secondaryAction.label}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EmptyState;
